/**
 * Services Contact page — /services/contact
 *
 * Shares the exact same `<LeadForm />` component that Systems/Contact uses
 * so every submission lands in the one `systems_leads` collection an admin
 * can triage from /admin/v2. The `kind="services-contact"` tag is what the
 * admin sidebar widget (Pending Leads) uses to route the ticket to the
 * Services team instead of Systems sales.
 */
import React from 'react';
import ServicesHeader from '../../components/ServicesHeader';
import ServicesFooter from '../../components/ServicesFooter';
import SeoHead from '../../components/SeoHead';
import LeadForm from '../../components/systems/LeadForm';
import { Mail, Clock, Phone, MapPin } from 'lucide-react';

export default function ServicesContact() {
  return (
    <div className="min-h-screen bg-white" data-testid="services-contact-page">
      <SeoHead
        path="/services/contact"
        title="Contact ZHELTA Services — Order, Warranty & Support Questions"
        description="Need help with an order, warranty claim, bulk quote, or product question? A ZHELTA Services technician will respond within 1 business day."
      />
      <ServicesHeader />

      <main className="py-20 md:py-24">
        <div className="zh-container max-w-5xl grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-10">
          {/* Intro + direct-channel info */}
          <div>
            <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">
              Services Support
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              How can we help?
            </h1>
            <p className="mt-5 text-[16px] text-gray-600 leading-relaxed">
              Order questions, warranty claims, bulk quotes, or technical advice — tell us what you
              need and a ZHELTA Services technician will respond within 1 business day.
            </p>

            <div className="mt-8 space-y-4 text-[14px] text-gray-700">
              <InfoRow
                icon={Mail}
                label="Email"
                value={
                  <a
                    className="text-[#c9a227] hover:text-[#f5a623]"
                    href="mailto:support@zhelta.com"
                    data-testid="services-contact-email-link"
                  >
                    support@zhelta.com
                  </a>
                }
              />
              <InfoRow
                icon={Phone}
                label="Phone"
                value={
                  <a
                    className="text-[#c9a227] hover:text-[#f5a623]"
                    href="tel:+19147681947"
                    data-testid="services-contact-phone-link"
                  >
                    914-768-1947
                  </a>
                }
              />
              <InfoRow icon={Clock} label="Response time" value="Under 1 business day" />
              <InfoRow icon={MapPin} label="Coverage" value="Worldwide shipping & support" />
            </div>
          </div>

          {/* Form */}
          <div>
            <LeadForm
              kind="services-contact"
              fields={['company', 'phone', 'message']}
              ctaLabel="Send message"
              successTitle="Message received"
              testIdPrefix="services-contact"
            />
          </div>
        </div>
      </main>

      <ServicesFooter />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#c9a227]" strokeWidth={2} />
      </div>
      <div>
        <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-gray-400">
          {label}
        </div>
        <div className="text-[14px] font-semibold text-gray-800 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
